import { Box, Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { ChevronDown, Filter, MoreVertical, Plus } from 'lucide-react';
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import { getNavFloatShadow } from 'src/components/Navbar/DesktopDrawer/desktop-drawer.styles';
import { OptionCheckbox, SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import {
  chevronSx,
  FilterChip,
  FilterChipCount,
  filterChipIconSx,
  OverflowButton,
  ScopeButton,
  ScopeHeading,
  ScopeLabel,
  Toolbar,
} from './top-controls.styles';

export type PolicyFilterMode = 'applicable' | 'all';

const filterModeLabels: Record<PolicyFilterMode, string> = {
  applicable: 'Applicable',
  all: 'All',
};

const CATEGORY_ALL = 'All';

// escalation policies are provisioned by useEnsureEscalationPolicies, never by hand
const isEscalationOnlyFilter = (selectedCategories: string[]) =>
  selectedCategories.length === 1 && selectedCategories[0] === POLICY_CATEGORY_ESCALATION;

// denser than the portal default; scoped here so other dropdowns keep their metrics
const policyMenuSx: SystemStyleObject<Theme> = {
  borderRadius: '0.625rem',
  minWidth: '11.875rem',
  backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
  boxShadow: (theme) => getNavFloatShadow(theme),
  // doubled selector: CommandRoot's rules are injected later and would win the tie
  '&& [cmdk-list]': { padding: '0.3125rem' },
  '&& [cmdk-item]': {
    gap: '0.5rem',
    padding: '0.375rem 0.5rem',
    borderRadius: '0.375rem',
  },
};

// SelectMenu values are strings, but the scope id is number | null
const GLOBAL_SCOPE_VALUE = '__global__';

const RowIcon = ({ children }: { children: ReactNode }) => (
  <Box
    aria-hidden
    sx={(theme) => ({
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      color: theme.palette.custom.OV.sys.onSurfaceVariant,
    })}
  >
    {children}
  </Box>
);

export interface SupportPolicyTopControlsProps {
  scopeLabel: string;
  scopeOptions: { id: number | null; label: string }[];
  scopeValue: number | null;
  onScopeChange: (id: number | null) => void;
  canChangeScope: boolean;
  policyFilterMode: PolicyFilterMode;
  onPolicyFilterModeChange: (mode: PolicyFilterMode) => void;
  totalPoliciesCount: number;
  applicablePoliciesCount: number;
  displayedPoliciesCount: number;
  categories: readonly string[];
  selectedCategories: string[];
  onCategoryToggle: (category: string) => void;
  categoryCounts: Record<string, number>;
  showArchived: boolean;
  setShowArchived: Dispatch<SetStateAction<boolean>>;
  archivedCount: number;
  hasAccessToManageSupportPolicies: boolean;
  onAddPolicy: () => void;
}

type ScopeControlProps = Pick<
  SupportPolicyTopControlsProps,
  'scopeLabel' | 'scopeOptions' | 'scopeValue' | 'onScopeChange' | 'canChangeScope'
>;

const ScopeControl = ({
  scopeLabel,
  scopeOptions,
  scopeValue,
  onScopeChange,
  canChangeScope,
}: ScopeControlProps) => {
  const options: SelectMenuOption[] = useMemo(() => {
    const globalEntry = scopeOptions.find((option) => option.id === null);
    const partnerEntries = scopeOptions.filter((option) => option.id !== null);
    const built: SelectMenuOption[] = [];
    if (globalEntry) {
      built.push({ value: GLOBAL_SCOPE_VALUE, title: globalEntry.label, group: 'Global' });
    }
    partnerEntries.forEach((option) => {
      built.push({ value: String(option.id), title: option.label, group: 'Partners' });
    });
    return built;
  }, [scopeOptions]);

  if (!canChangeScope) {
    return <ScopeHeading>{scopeLabel}</ScopeHeading>;
  }

  return (
    <SelectMenu
      label="Policy scope"
      search
      searchPlaceholder="Search partners"
      options={options}
      sectioned
      value={scopeValue === null ? GLOBAL_SCOPE_VALUE : String(scopeValue)}
      onChange={(value) => onScopeChange(value === GLOBAL_SCOPE_VALUE ? null : Number(value))}
      menuWidth="16rem"
      menuSx={policyMenuSx}
      renderTrigger={({ open, getTriggerProps }) => (
        <ScopeButton {...getTriggerProps()} type="button">
          <ScopeLabel>{scopeLabel}</ScopeLabel>
          <Box aria-hidden sx={chevronSx(open)}>
            <ChevronDown size={13} />
          </Box>
        </ScopeButton>
      )}
    />
  );
};

type FilterControlProps = Pick<
  SupportPolicyTopControlsProps,
  | 'policyFilterMode'
  | 'onPolicyFilterModeChange'
  | 'totalPoliciesCount'
  | 'applicablePoliciesCount'
  | 'displayedPoliciesCount'
  | 'categories'
  | 'selectedCategories'
  | 'onCategoryToggle'
  | 'categoryCounts'
  | 'showArchived'
  | 'setShowArchived'
  | 'archivedCount'
>;

const FilterControl = ({
  policyFilterMode,
  onPolicyFilterModeChange,
  totalPoliciesCount,
  applicablePoliciesCount,
  displayedPoliciesCount,
  categories,
  selectedCategories,
  onCategoryToggle,
  categoryCounts,
  showArchived,
  setShowArchived,
  archivedCount,
}: FilterControlProps) => {
  const [open, setOpen] = useState(false);

  const selectedRealCategories = useMemo(
    () => selectedCategories.filter((category) => category !== CATEGORY_ALL),
    [selectedCategories],
  );

  const isLit =
    policyFilterMode !== 'applicable' || selectedRealCategories.length > 0 || showArchived;

  const title = useMemo(() => {
    const axisLabels = [
      ...(policyFilterMode !== 'applicable' ? [filterModeLabels.all] : []),
      ...selectedRealCategories,
    ];
    if (axisLabels.length === 0 && !showArchived) return 'Display options';
    const parts = [axisLabels.join(', '), showArchived ? 'archived shown' : ''].filter(
      Boolean,
    );
    return `Display options — ${parts.join(' · ')}`;
  }, [policyFilterMode, selectedRealCategories, showArchived]);

  const options: SelectMenuOption[] = useMemo(() => {
    const showOptions: SelectMenuOption[] = [
      {
        value: 'mode-applicable',
        title: filterModeLabels.applicable,
        hint: String(applicablePoliciesCount),
        group: 'Show',
        adornment: <OptionCheckbox checked={policyFilterMode === 'applicable'} />,
        onSelect: () => {
          onPolicyFilterModeChange('applicable');
          setOpen(false);
        },
      },
      {
        value: 'mode-all',
        title: filterModeLabels.all,
        hint: String(totalPoliciesCount),
        group: 'Show',
        adornment: <OptionCheckbox checked={policyFilterMode === 'all'} />,
        onSelect: () => {
          onPolicyFilterModeChange('all');
          setOpen(false);
        },
      },
    ];

    const categoryOptions: SelectMenuOption[] = categories
      .filter((category) => category !== CATEGORY_ALL)
      .map((category) => ({
        value: `category-${category}`,
        title: category,
        hint: String(categoryCounts[category] ?? 0),
        group: 'Category',
        adornment: <OptionCheckbox checked={selectedCategories.includes(category)} />,
        onSelect: () => onCategoryToggle(category),
      }));

    const archivedOption: SelectMenuOption = {
      value: 'show-archived',
      title: 'Show archived',
      hint: String(archivedCount),
      adornment: <OptionCheckbox checked={showArchived} />,
      onSelect: () => setShowArchived((prev) => !prev),
    };

    return [...showOptions, ...categoryOptions, archivedOption];
  }, [
    policyFilterMode,
    applicablePoliciesCount,
    totalPoliciesCount,
    categories,
    categoryCounts,
    selectedCategories,
    archivedCount,
    showArchived,
    onPolicyFilterModeChange,
    onCategoryToggle,
    setShowArchived,
  ]);

  return (
    <SelectMenu
      label="Display options"
      options={options}
      sectioned
      open={open}
      onOpenChange={setOpen}
      menuWidth="14rem"
      menuSx={policyMenuSx}
      renderTrigger={({ open: menuOpen, getTriggerProps }) => (
        <FilterChip {...getTriggerProps()} type="button" lit={isLit} title={title}>
          <Box aria-hidden sx={filterChipIconSx}>
            <Filter size={13} />
          </Box>
          <span>{filterModeLabels[policyFilterMode]}</span>
          <FilterChipCount>{displayedPoliciesCount}</FilterChipCount>
          <Box aria-hidden sx={chevronSx(menuOpen)}>
            <ChevronDown size={13} />
          </Box>
        </FilterChip>
      )}
    />
  );
};

const OverflowControl = ({ onAddPolicy }: { onAddPolicy: () => void }) => {
  const [open, setOpen] = useState(false);

  const options: SelectMenuOption[] = [
    {
      value: 'add-policy',
      title: 'Add policy',
      adornment: (
        <RowIcon>
          <Plus size={14} />
        </RowIcon>
      ),
      onSelect: () => {
        onAddPolicy();
        setOpen(false);
      },
    },
  ];

  return (
    <SelectMenu
      label="More actions"
      options={options}
      open={open}
      onOpenChange={setOpen}
      menuWidth="10rem"
      menuSx={policyMenuSx}
      renderTrigger={({ getTriggerProps }) => (
        <OverflowButton {...getTriggerProps()} type="button" aria-label="More actions">
          <MoreVertical size={16} />
        </OverflowButton>
      )}
    />
  );
};

export const SupportPolicyTopControls = ({
  scopeLabel,
  scopeOptions,
  scopeValue,
  onScopeChange,
  canChangeScope,
  policyFilterMode,
  onPolicyFilterModeChange,
  totalPoliciesCount,
  applicablePoliciesCount,
  displayedPoliciesCount,
  categories,
  selectedCategories,
  onCategoryToggle,
  categoryCounts,
  showArchived,
  setShowArchived,
  archivedCount,
  hasAccessToManageSupportPolicies,
  onAddPolicy,
}: SupportPolicyTopControlsProps) => {
  // the whole toolbar has always been manage-gated, long before it held a scope
  // picker — view-only users are not losing a control they had. Giving them the
  // scope and filter controls back is its own change, tracked separately.
  if (!hasAccessToManageSupportPolicies) return null;

  return (
    <Toolbar>
      <ScopeControl
        scopeLabel={scopeLabel}
        scopeOptions={scopeOptions}
        scopeValue={scopeValue}
        onScopeChange={onScopeChange}
        canChangeScope={canChangeScope}
      />
      <FilterControl
        policyFilterMode={policyFilterMode}
        onPolicyFilterModeChange={onPolicyFilterModeChange}
        totalPoliciesCount={totalPoliciesCount}
        applicablePoliciesCount={applicablePoliciesCount}
        displayedPoliciesCount={displayedPoliciesCount}
        categories={categories}
        selectedCategories={selectedCategories}
        onCategoryToggle={onCategoryToggle}
        categoryCounts={categoryCounts}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        archivedCount={archivedCount}
      />
      {isEscalationOnlyFilter(selectedCategories) ? null : (
        <OverflowControl onAddPolicy={onAddPolicy} />
      )}
    </Toolbar>
  );
};
